/* ============================================
   QUOTE DASHBOARD — Lead Actions (Hold / Wake / Mark Dead / Resurrect / Actions Chooser)
   Asset: dashboard-lead-actions.js

   Extracted from dashboard-main.js on 2026-05-21 as Phase A.2 of the
   dashboard refactor. Loaded via <script defer> in
   sections/rep-quote-dashboard.liquid BEFORE main.js so the RQD bindings
   exist by the time main runs.

   Public API (attached to window.RQD):
     handleMarkLeadDead(btn)
     handleResurrectLead(btn)
     openLeadActionsModal(q, opts)
     handleHoldLead(btn)
     handleWakeLead(btn)
     openHoldDateModal(ref, holdUntil, reason, isResumed, isPending)

   Init contract (RQD.initLeadActions called once by main.js at boot):
     esc, formatDate, formatFollowUpDate, daysFromNowYmd, snapYmdToWeekday,
     fetchQuotes, _qstHasContactActivity, getAllQuotes (closure-getter),
     API, Auth (defaulted from window.RepPortal if omitted)

   Internal cross-references between extracted functions use BARE names
   (sibling functions in this IIFE — same scope as before extraction).
   Only deps from main.js's IIFE go through the init-deps surface.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initLeadActions (from main.js boot)
  var esc, formatDate, formatFollowUpDate, daysFromNowYmd, snapYmdToWeekday;
  var fetchQuotes, _qstHasContactActivity, getAllQuotes;
  var API, Auth;
  /* Ryan 2026-05-22 audit hotfix — Phase A.2 missed this dep. The lead-
     actions modal's "Create Quote" button (action === 'create-quote' at
     L300 below) calls handleBuildQuoteFromLead, which lives in main.js
     (~L13316) — function declaration, safe to pass directly. Latent
     since 2026-05-21; would throw "handleBuildQuoteFromLead is not
     defined" on NEW LEAD / SAMPLE / ON HOLD modal Create Quote click. */
  var handleBuildQuoteFromLead;

  // === handleMarkLeadDead — extracted from main.js:15543-15555 ===
    function handleMarkLeadDead(btn) {
      var ref = btn && btn.dataset ? btn.dataset.ref : null;
      if (!ref) return;
      document.querySelectorAll('.rqd-actions-menu--open').forEach(function(m) { m.classList.remove('rqd-actions-menu--open'); });
      // Resolve allQuotes via the injected getter (closure over main.js's IIFE state).
      var allQuotes = getAllQuotes ? getAllQuotes() : [];
      var q = (typeof allQuotes !== 'undefined') ? allQuotes.find(function(x) { return x.reference === ref; }) : null;
      if (!q) {
        // Stale tab — the row we just clicked isn't in allQuotes anymore.
        // Refresh and abort; user can click again.
        if (typeof fetchQuotes === 'function') fetchQuotes();
        return;
      }
      openLeadActionsModal(q, { initialView: 'mark-dead' });
    }

  // === handleResurrectLead — extracted from main.js:15557-15573 ===
    async function handleResurrectLead(btn) {
      var ref = btn.dataset.ref;
      document.querySelectorAll('.rqd-actions-menu--open').forEach(function(m) { m.classList.remove('rqd-actions-menu--open'); });
      if (!confirm('Resurrect ' + ref + ' as a NEW LEAD? It will go back into your active queue.')) return;
      var origText = btn.textContent;
      btn.textContent = 'Resurrecting…';
      btn.disabled = true;
      try {
        await API.post('/lead-resurrect', { ref: ref });
        fetchQuotes();
      } catch (err) {
        btn.textContent = origText;
        btn.disabled = false;
        var msg = err && err.message ? err.message : 'Unknown error';
        alert('Could not resurrect: ' + msg);
      }
    }

  // === openLeadActionsModal — extracted from main.js:15810-16073 ===
    function openLeadActionsModal(q, opts) {
      opts = opts || {};
      var existing = document.getElementById('rqd-lead-actions-modal');
      if (existing) existing.remove();
      if (!q) return;
      var sess = window.RepPortal && window.RepPortal.Auth.getSession();
      var isAdminUser = sess && sess.admin;
      /* Ryan 2026-05-21: Activity gate replaces 72h age gate. Mark Dead is
         only listed in this chooser when the lead has at least one rep-typed
         Activity entry (or is sample/onHold which bypass). */
      var canMarkDead = !q.isDeadLead && (q.isSample || q.isOnHold || _qstHasContactActivity(q));

      var isPendingRow = q.isPending || q.status === 'pending';
      // Heading reflects current state.
      var heading;
      if (q.isDeadLead) heading = 'Dead Lead — actions';
      else if (q.isSample) heading = 'Sample Lead — actions';
      else if (q.isOnHold) heading = 'On Hold — actions';
      else if (isPendingRow) heading = 'Pending Deal — actions';
      else heading = 'New Lead — actions';

      var sub;
      if (q.isOnHold && q.holdUntil) sub = 'Currently held until ' + esc(formatDate(q.holdUntil)) + '.';
      else if (q.isDeadLead) sub = 'Bring this lead back into the pipeline or set a hold date.';
      else if (q.isSample) sub = 'Cold-pool sample — promote, hold, or kill.';
      else if (isPendingRow) sub = 'Build the quote when ready, or delete if it&apos;s gone cold.';
      else sub = 'Pick a state for ' + esc(q.reference) + '.';

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Create Quote icon
         flipped from ✏ (pencil — implies "edit existing", which is
         misleading since the quote doesn't exist yet) to 📝 (memo +
         pencil — reads as "draft a new document", which matches what
         the action actually does). */
      // Build button list per state. data-action drives the click router.
      var btns = [];
      if (q.isDeadLead) {
        btns.push({ action: 'resurrect', label: '⏵ Resurrect Lead', primary: true });
        btns.push({ action: 'hold',      label: '⏸ Put On Hold',     hold: true });
      } else if (q.isOnHold) {
        /* Ryan 2026-05-16: ON HOLD rows can skip the "remove hold →
           resumed → quote it" two-step and jump straight to building
           a quote. Create Quote is the primary intended path here too
           (matches NEW LEAD and SAMPLE branches). Remove Hold is the
           secondary action (renamed from "Wake Now" — the result is
           a RESUMED lead, "remove" is more accurate user-facing). */
        btns.push({ action: 'create-quote', label: '📝 Create Quote',     primary: true });
        btns.push({ action: 'wake',         label: '⏵ Remove Hold' });
        btns.push({ action: 'edit-hold',    label: '⏸ Edit Hold Date',   hold: true });
        if (canMarkDead) btns.push({ action: 'mark-dead', label: '💀 Mark Dead' });
      } else if (q.isSample) {
        // Samples are treated like NEW LEADs — same actions, no separate
        // promote step. Rep quotes them when ready, holds, or marks dead.
        // Create Quote is the primary intended path (matches the cog menu).
        btns.push({ action: 'create-quote', label: '📝 Create Quote',  primary: true });
        btns.push({ action: 'hold',         label: '⏸ Put On Hold' });
        if (canMarkDead) btns.push({ action: 'mark-dead', label: '💀 Mark Dead' });
      } else if (isPendingRow) {
        /* Ryan 2026-05-16: PENDING rows in the chooser. Two actions:
           Create Quote (primary) + Put On Hold / Edit Hold Date (the
           latter when an existing follow-up is attached — same edit/
           fresh-hold distinction the lead-state rows use). Delete
           still lives on the inline row buttons + cog menu, so it's
           not duplicated here. Mark Dead doesn't apply (pending isn't
           a lead in the cascade). */
        var _pendingHasFollowUp = !!(q.followUp && q.followUp.dueAt);
        btns.push({ action: 'create-quote-pending', label: '📝 Create Quote',  primary: true });
        btns.push({
          action: _pendingHasFollowUp ? 'edit-hold' : 'hold',
          label:  _pendingHasFollowUp ? '⏸ Edit Hold Date' : '⏸ Put On Hold',
          hold: true,
        });
      } else {
        // NEW LEAD — no quote exists yet either; same primary affordance.
        btns.push({ action: 'create-quote', label: '📝 Create Quote',  primary: true });
        btns.push({ action: 'hold',         label: '⏸ Put On Hold' });
        if (canMarkDead) btns.push({ action: 'mark-dead', label: '💀 Mark Dead' });
      }

      function btnHtml(b) {
        var cls = 'rqd-lead-action';
        if (b.primary) cls += ' rqd-lead-action--primary';
        if (b.hold) cls += ' rqd-lead-action--hold';
        if (b.action === 'mark-dead') cls += ' rqd-lead-action--dead';
        if (b.action === 'resurrect' || b.action === 'wake') cls += ' rqd-lead-action--alive';
        if (b.action === 'promote') cls += ' rqd-lead-action--sample';
        return '<button type="button" class="' + cls + '" data-action="' + esc(b.action) + '">' + b.label + '</button>';
      }

      var overlay = document.createElement('div');
      overlay.id = 'rqd-lead-actions-modal';
      overlay.className = 'rqd-daterange-backdrop';
      overlay.innerHTML = ''
        + '<div class="rqd-daterange-modal" role="dialog" aria-label="Lead actions" style="max-width:420px;">'
        +   '<div class="rqd-daterange-modal__header">'
        +     '<div class="rqd-daterange-modal__title" id="rqd-lead-actions-title"></div>'
        +     '<div class="rqd-daterange-modal__summary" id="rqd-lead-actions-sub"></div>'
        +   '</div>'
        +   '<div class="rqd-lead-actions-body" id="rqd-lead-actions-body" style="padding:18px 22px;display:flex;flex-direction:column;gap:8px;"></div>'
        +   '<div class="rqd-daterange-modal__footer">'
        +     '<button type="button" class="rqd-pill" id="rqd-lead-actions-cancel">Close</button>'
        +   '</div>'
        + '</div>';
      var portalWrap = document.querySelector('.rep-quote-portal-wrap') || document.body;
      portalWrap.appendChild(overlay);

      var titleEl  = overlay.querySelector('#rqd-lead-actions-title');
      var subEl    = overlay.querySelector('#rqd-lead-actions-sub');
      var bodyEl   = overlay.querySelector('#rqd-lead-actions-body');
      var cancelEl = overlay.querySelector('#rqd-lead-actions-cancel');

      // Track current view + whether the buttons view is reachable from
      // mark-dead view (it isn't when cog menu launched us directly into
      // mark-dead — in that case the cancel button just closes the modal).
      var _currentView = 'buttons';

      function close() {
        overlay.remove();
        document.removeEventListener('keydown', onEsc);
      }
      function onEsc(ev) { if (ev.key === 'Escape') close(); }
      document.addEventListener('keydown', onEsc);

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21) — View swap: buttons
         list ↔ mark-dead reason form. Replaces the prior body-mounted
         popover that flew to the top-left because its anchor element was
         removed before positioning. Both views share the same overlay /
         header / footer chrome; only body + title + sub change. */
      function showButtonsView() {
        _currentView = 'buttons';
        titleEl.textContent = heading;
        subEl.innerHTML = sub; // sub may contain HTML entities (esc'd)
        bodyEl.innerHTML = btns.map(btnHtml).join('');
        cancelEl.textContent = 'Close';
      }

      function showMarkDeadView() {
        _currentView = 'mark-dead';
        titleEl.textContent = '💀 Mark ' + q.reference + ' as DEAD?';
        subEl.textContent = 'Add a reason — what made this lead dead?';
        bodyEl.innerHTML = ''
          + '<textarea class="rqd-mark-dead-reason" rows="3" placeholder="e.g. went with competitor, project on hold indefinitely, customer never responded after 3 calls"></textarea>'
          + '<div class="rqd-mark-dead-error">Reason is required.</div>'
          + '<button type="button" class="rqd-mark-dead-confirm">💀 Mark Dead</button>';
        cancelEl.textContent = (opts.initialView === 'mark-dead') ? 'Cancel' : '← Back';

        var textarea  = bodyEl.querySelector('.rqd-mark-dead-reason');
        var errorEl   = bodyEl.querySelector('.rqd-mark-dead-error');
        var confirmBtn = bodyEl.querySelector('.rqd-mark-dead-confirm');

        setTimeout(function() { try { textarea.focus(); } catch (e) {} }, 30);

        textarea.addEventListener('input', function() {
          overlay.classList.remove('rqd-mark-dead-invalid');
          errorEl.textContent = 'Reason is required.';
        });
        textarea.addEventListener('keydown', function(ev) {
          if ((ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') {
            ev.preventDefault();
            confirmBtn.click();
          }
        });

        confirmBtn.addEventListener('click', async function() {
          var reason = (textarea.value || '').trim();
          if (!reason) {
            overlay.classList.add('rqd-mark-dead-invalid');
            try { textarea.focus(); } catch (e) {}
            return;
          }
          confirmBtn.textContent = 'Killing…';
          confirmBtn.disabled = true;
          cancelEl.disabled = true;
          try {
            await API.post('/lead-mark-dead', { ref: q.reference, reason: reason });
            close();
            fetchQuotes({ quiet: true });
          } catch (err) {
            var msg = err && err.message ? err.message : 'Unknown error';
            confirmBtn.textContent = '💀 Mark Dead';
            confirmBtn.disabled = false;
            cancelEl.disabled = false;
            errorEl.textContent = msg;
            overlay.classList.add('rqd-mark-dead-invalid');
          }
        });
      }

      // Initial render
      if (opts.initialView === 'mark-dead') {
        showMarkDeadView();
      } else {
        showButtonsView();
      }

      overlay.addEventListener('click', function (e) {
        // Cancel button: in mark-dead view, may go back to buttons view (when
        // we entered via buttons) or close the modal (when cog menu launched
        // us directly into mark-dead view).
        if (e.target === cancelEl) {
          if (_currentView === 'mark-dead' && opts.initialView !== 'mark-dead') {
            showButtonsView();
          } else {
            close();
          }
          return;
        }
        // Backdrop click closes from any view
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — both ends of the gesture must be on the backdrop; a drag out of the dialog (selecting a quantity) must not close it. See custom-rep-portal.js. */
        if (window.RepPortal.dismissedOnBackdrop(e, overlay)) {
          close();
          return;
        }
        var actionBtn = e.target.closest('.rqd-lead-action');
        if (!actionBtn) return;
        var action = actionBtn.dataset.action;
        var fakeBtn = { dataset: { ref: q.reference }, textContent: actionBtn.textContent, disabled: false };

        if (action === 'mark-dead') {
          /* Ryan 2026-05-21: Activity gate already enforced when building
             the button list (canMarkDead). At this point the click is
             allowed — swap the modal body to the reason view in place. */
          showMarkDeadView();
          return;
        }
        if (action === 'hold' || action === 'edit-hold') { close(); handleHoldLead(fakeBtn); return; }
        if (action === 'wake')         { close(); handleWakeLead(fakeBtn); return; }
        if (action === 'resurrect')    { close(); handleResurrectLead(fakeBtn); return; }
        if (action === 'promote')      { close(); handlePromoteSample(fakeBtn); return; }
        if (action === 'create-quote') { close(); handleBuildQuoteFromLead(fakeBtn); return; }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — PENDING actions.
           Create Quote on a pending row is a direct nav to the workstation
           with ?pending=1 (the workstation handles the pending → quote
           promotion server-side on first save), NOT a /create-quote-from-
           lead API call (pending isn't a lead record). Delete reuses the
           existing delete-pending button handler via a synthetic click. */
        if (action === 'create-quote-pending') {
          close();
          window.location.href = '/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&pending=1&from=dashboard';
          return;
        }
        if (action === 'delete-pending') {
          close();
          // Reuse the row's existing Delete handler by simulating a click
          // on the inline button (which already has the right confirm
          // prompt + API call). Falls back to direct API.post if the
          // inline button isn't in the DOM (collapsed row, narrow view).
          var inlineDel = document.querySelector('.rqd-delete-pending-btn[data-ref="' + q.reference + '"]');
          if (inlineDel) inlineDel.click();
          else {
            if (!confirm('Delete pending deal ' + q.reference + '?')) return;
            API.post('/delete-pending', { ref: q.reference })
              .then(function() { fetchQuotes(); })
              .catch(function(err) { alert('Failed: ' + (err && err.message || 'Unknown error')); });
          }
          return;
        }
      });
    }

    // ── ON HOLD: hold + wake handlers + date picker modal ──
    // Universal "deferred follow-up" lifecycle — distinct from DEAD (dead =
    // not coming back) and SAMPLE (sample = cold pool noise). The rep
    // picks a wake date; the slot is freed; the lead auto-resurrects on
    // the dashboard's next read after holdUntil ≤ today.


  // === handleHoldLead — extracted from main.js:16074-16097 ===
    function handleHoldLead(btn) {
      var ref = btn.dataset.ref;
      document.querySelectorAll('.rqd-actions-menu--open').forEach(function(m) { m.classList.remove('rqd-actions-menu--open'); });
      // Resolve allQuotes via the injected getter (closure over main.js's IIFE state).
      var allQuotes = getAllQuotes ? getAllQuotes() : [];
      var q = allQuotes.find(function (x) { return x.reference === ref; });
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Pending records
         don't carry holdUntil / holdReason (those are lead-state fields);
         their follow-up data lives on q.followUp. Read from the right
         spot so "Edit Hold Date" pre-fills correctly. */
      var _isPending = !!(q && (q.isPending || q.status === 'pending'));
      var existingHoldUntil, existingReason;
      if (_isPending) {
        existingHoldUntil = (q.followUp && q.followUp.dueAt) ? q.followUp.dueAt : '';
        existingReason = (q.followUp && q.followUp.reason) ? q.followUp.reason : '';
      } else {
        existingHoldUntil = q && q.holdUntil ? q.holdUntil : '';
        existingReason = q && q.holdReason ? q.holdReason : '';
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — RESUMED leads
         (wokenFromHold flag set, still in new_lead status) get the
         longer preset shelf + +7d default in the hold modal. Pass the
         flag through so openHoldDateModal can branch. */
      var _isResumed = !!(q && q.wokenFromHold && (q.status === 'new_lead' || q.isLead) && !q.isSample);
      openHoldDateModal(ref, existingHoldUntil, existingReason, _isResumed, _isPending);
    }

  // === handleWakeLead — extracted from main.js:16099-16127 ===
    async function handleWakeLead(btn) {
      var ref = btn.dataset.ref;
      document.querySelectorAll('.rqd-actions-menu--open').forEach(function(m) { m.classList.remove('rqd-actions-menu--open'); });
      /* Ryan 2026-05-16: confirm() removed. Waking is non-destructive
         (auto-wake fires on every dashboard fetch once holdUntil ≤
         today anyway) and the action button itself is the
         confirmation — extra modal felt like friction. */
      var origText = btn.textContent;
      btn.textContent = 'Waking…';
      btn.disabled = true;
      try {
        await API.post('/lead-wake', { ref: ref });
        // Ryan 2026-05-21: quiet refresh so the row stays expanded and
        // the page doesn't jump to the top after the lead wakes.
        fetchQuotes({ quiet: true });
      } catch (err) {
        btn.textContent = origText;
        btn.disabled = false;
        var msg = err && err.message ? err.message : 'Unknown error';
        alert('Could not wake: ' + msg);
      }
    }

    // Save (or clear, when isClear=true) the sample shipment tracking record.
    // Reads carrier/number/url from the inline inputs on the sample detail
    // panel; sends POST /sample-tracking-save with empty number to clear.
    // Quote-level sample tracking save — same shape as the lead-version
    // below but hits /quote-sample-tracking-save (ECORE-gated, writes to
    // workstation_meta:{ref}.sampleTracking).

  // === openHoldDateModal — extracted from main.js:16495-16761 ===
    function openHoldDateModal(ref, existingHoldUntil, existingReason, isResumed, isPending) {
      var existing = document.getElementById('rqd-hold-modal');
      if (existing) existing.remove();

      /* Ryan 2026-05-16: Drop-In is a "what just happened" type (rep
         physically stopped by) — it has no place in a "put on hold"
         flow which is forward-looking. Chip set narrowed to 4 here. */
      var _chipDefs = [
        { type: 'note',    emoji: '💬', label: 'Note' },
        { type: 'call',    emoji: '📞', label: 'Call' },
        { type: 'email',   emoji: '📧', label: 'Email' },
        { type: 'meeting', emoji: '🤝', label: 'Meeting' },
      ];
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Preset shelf +
         default-arm policy in the hold modal:
           - RESUMED  → long shelf, +7d default (already had a touch).
           - PENDING  → long shelf, +14d default (longer-cadence note-
                       to-self about a future quote).
           - else     → short shelf, +2d default (fresh lead chase).
         Pending and Resumed share the long shelf — both have moved past
         the initial chase window and want longer-cadence options. */
      var _useLongShelf = isResumed || isPending;
      var _presetDays = _useLongShelf ? [7, 14, 30, 60, 90, 180] : [1, 2, 3, 4, 5];
      var _defaultArmDays = isPending ? 14 : (isResumed ? 7 : 2);
      var _isEditing = !!existingHoldUntil;
      var _armedYmd = existingHoldUntil || daysFromNowYmd(_defaultArmDays);
      var _armedLabel = formatFollowUpDate(_armedYmd);
      /* Ryan 2026-05-16: When editing an existing hold (rep clicked
         "Edit Hold Date"), the button reads "Edit Hold Date" since the
         action is an EDIT, not a fresh hold. Fresh holds keep "Put on
         Hold". Textarea is intentionally NOT pre-filled with the old
         reason — pre-filling encourages duplicate-text notes when the
         rep just clicks Save without retyping. Empty textarea pushes
         the rep to write a fresh reason for the edit (or fall through
         to the auto-text "Put on hold until <date>." fallback). */
      var _modalSubmitLabel = _isEditing ? 'Edit Hold Date' : 'Put on Hold';
      var _modalTitle = _isEditing ? '⏸ Edit Hold Date' : '⏸ Put on Hold';
      var _modalSummary = _isEditing
        ? esc(ref) + ' — change the wake date + log why.'
        : esc(ref) + ' — write a quick note + pick when to come back.';

      /* At initial render: find the lowest-N preset whose date matches
         _armedYmd and highlight only that one (anti-double-highlight on
         weekend-snap days). Same rule the inline armFollowUp uses for
         non-click paths. — Ryan 2026-05-16. */
      var _armedDays = NaN;
      _presetDays.forEach(function(d) {
        if (daysFromNowYmd(d) === _armedYmd && (!isFinite(_armedDays) || d < _armedDays)) {
          _armedDays = d;
        }
      });
      var presetsHtml = _presetDays.map(function(d) {
        var activeCls = (d === _armedDays) ? ' rqd-notes-log__followup-pick--active' : '';
        return '<button type="button" class="rqd-notes-log__followup-pick' + activeCls + '" data-days="' + d + '">+' + d + 'd</button>';
      }).join('');
      var chipsHtml = _chipDefs.map(function(c) {
        var active = c.type === 'note' ? ' rqd-notes-log__chip--active' : '';
        return '<button type="button" class="rqd-notes-log__chip' + active + '" data-note-type="' + esc(c.type) + '" aria-pressed="' + (c.type === 'note' ? 'true' : 'false') + '">' + c.emoji + ' <span class="rqd-notes-log__chip-label">' + esc(c.label) + '</span></button>';
      }).join('');

      var overlay = document.createElement('div');
      overlay.id = 'rqd-hold-modal';
      overlay.className = 'rqd-daterange-backdrop';
      overlay.innerHTML = ''
        + '<div class="rqd-daterange-modal rqd-hold-modal-v2' + (isPending ? ' rqd-hold-modal-v2--pending' : '') + '" role="dialog" aria-label="' + esc(_modalSubmitLabel) + '" style="max-width:640px;">'
        +   '<div class="rqd-daterange-modal__header">'
        +     '<div class="rqd-daterange-modal__title">' + _modalTitle + '</div>'
        +     '<div class="rqd-daterange-modal__summary">' + _modalSummary + '</div>'
        +   '</div>'
        +   '<div class="rqd-notes-log rqd-hold-modal-v2__body" data-ref="' + esc(ref) + '" style="padding:18px 22px;">'
        +     '<div class="rqd-notes-log__composer" style="margin-bottom:0;">'
        +       '<div class="rqd-notes-log__layout">'
        +         '<div class="rqd-notes-log__types rqd-notes-log__chips" role="group" aria-label="Note type">' + chipsHtml + '</div>'
        +         '<div class="rqd-notes-log__input-col">'
        +           '<textarea class="rqd-notes-log__input" rows="3" placeholder="' + (_isEditing ? 'Why are you changing the wake date?' : 'Reason for the hold…') + '"></textarea>'
        +         '</div>'
        +         '<div class="rqd-notes-log__followup" data-armed-date="' + esc(_armedYmd) + '">'
        +           '<div class="rqd-notes-log__followup-header">'
        +             '<span class="rqd-notes-log__followup-title">Wake date</span>'
        +             '<span class="rqd-notes-log__followup-armed-date">— ' + esc(_armedLabel) + '</span>'
        +           '</div>'
        +           '<div class="rqd-notes-log__followup-presets">' + presetsHtml + '</div>'
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Calendar always
           rendered. The short preset shelf is a nudge for the typical
           chase cadence; the date input handles outlier customers who
           want a long follow-up window from day one. */
        +           '<input type="date" class="rqd-notes-log__followup-custom" value="' + esc(_armedYmd) + '" aria-label="Custom wake date">'
        +         '</div>'
        +       '</div>'
        +     '</div>'
        +   '</div>'
        +   '<div class="rqd-daterange-modal__footer">'
        +     '<button type="button" class="rqd-pill" id="rqd-hold-cancel">Cancel</button>'
        +     '<button type="button" class="rqd-notes-log__submit" id="rqd-hold-apply" style="width:auto;min-width:160px;">' + esc(_modalSubmitLabel) + '</button>'
        +   '</div>'
        + '</div>';
      var portalWrap = document.querySelector('.rep-quote-portal-wrap') || document.body;
      portalWrap.appendChild(overlay);

      var cluster = overlay.querySelector('.rqd-notes-log__followup');
      var ta = overlay.querySelector('.rqd-notes-log__input');

      /* Hold modal version of armFollowUp — mirrors the inline one's
         anti-double-highlight logic. Optional `days` arg: when the rep
         clicks a specific preset, only that preset highlights. Without
         days (e.g., future custom date input path), highlight the
         lowest-N preset whose date resolves to ymd. — Ryan 2026-05-16. */
      function _setArmedDate(ymd, days) {
        if (!ymd) return;
        cluster.dataset.armedDate = ymd;
        var picks = cluster.querySelectorAll('.rqd-notes-log__followup-pick');
        var explicitDays = (days !== undefined && days !== null) ? parseInt(days, 10) : NaN;
        var lowestMatchingDays = NaN;
        if (!isFinite(explicitDays)) {
          picks.forEach(function(b) {
            var d = parseInt(b.dataset.days, 10);
            if (!isNaN(d) && daysFromNowYmd(d) === ymd) {
              if (!isFinite(lowestMatchingDays) || d < lowestMatchingDays) lowestMatchingDays = d;
            }
          });
        }
        picks.forEach(function(b) {
          var d = parseInt(b.dataset.days, 10);
          var match = isFinite(explicitDays) ? (d === explicitDays) : (isFinite(lowestMatchingDays) && d === lowestMatchingDays);
          b.classList.toggle('rqd-notes-log__followup-pick--active', match);
        });
        var dateEl = cluster.querySelector('.rqd-notes-log__followup-armed-date');
        if (dateEl) dateEl.textContent = '— ' + formatFollowUpDate(ymd);
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Sync the
           calendar input when a preset is clicked, so the date picker
           reflects the active armed date. Skip if the input is already
           in sync (avoids redundant value writes that re-trigger
           input events in some browsers). */
        var customInp = cluster.querySelector('.rqd-notes-log__followup-custom');
        if (customInp && customInp.value !== ymd) customInp.value = ymd;
      }

      overlay.addEventListener('click', async function(e) {
        // Type chip toggle — mirror inline Notes behavior (note exclusive default,
        // others multi-select).
        var chip = e.target.closest('.rqd-notes-log__chip');
        if (chip && chip.closest('.rqd-notes-log__types')) {
          var chipsRow = chip.parentElement;
          var clickedType = chip.dataset.noteType;
          if (clickedType === 'note') {
            chipsRow.querySelectorAll('.rqd-notes-log__chip').forEach(function(c) {
              var isNote = c.dataset.noteType === 'note';
              c.classList.toggle('rqd-notes-log__chip--active', isNote);
              c.setAttribute('aria-pressed', isNote ? 'true' : 'false');
            });
          } else {
            var willActivate = !chip.classList.contains('rqd-notes-log__chip--active');
            chip.classList.toggle('rqd-notes-log__chip--active', willActivate);
            chip.setAttribute('aria-pressed', willActivate ? 'true' : 'false');
            var noteChipEl = chipsRow.querySelector('.rqd-notes-log__chip[data-note-type="note"]');
            var anyOtherActive = Array.prototype.some.call(
              chipsRow.querySelectorAll('.rqd-notes-log__chip'),
              function(c) { return c.dataset.noteType !== 'note' && c.classList.contains('rqd-notes-log__chip--active'); }
            );
            if (noteChipEl) {
              noteChipEl.classList.toggle('rqd-notes-log__chip--active', !anyOtherActive);
              noteChipEl.setAttribute('aria-pressed', anyOtherActive ? 'false' : 'true');
            }
          }
          if (ta) ta.focus();
          return;
        }
        // Preset toggle.
        var preset = e.target.closest('.rqd-notes-log__followup-pick');
        if (preset) {
          var days = parseInt(preset.dataset.days, 10);
          if (isFinite(days)) _setArmedDate(daysFromNowYmd(days), days);
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — both ends of the gesture must be on the backdrop; a drag out of the dialog (selecting a quantity) must not close it. See custom-rep-portal.js. */
        if (e.target.id === 'rqd-hold-cancel' || window.RepPortal.dismissedOnBackdrop(e, overlay)) {
          overlay.remove();
          document.removeEventListener('keydown', onEsc);
          return;
        }
        if (e.target.id === 'rqd-hold-apply' || e.target.closest('#rqd-hold-apply')) {
          var btn = overlay.querySelector('#rqd-hold-apply');
          var holdUntil = cluster.dataset.armedDate || '';
          if (!holdUntil) { alert('Pick a wake date.'); return; }
          var text = (ta && ta.value ? ta.value : '').trim();
          // Worker requires text on /note-entry-add — fall back to an
          // auto-text so blank-reason holds still get a coherent log entry.
          if (!text) text = 'Put on hold until ' + holdUntil + '.';
          var activeTypes = [];
          overlay.querySelectorAll('.rqd-notes-log__types .rqd-notes-log__chip--active').forEach(function(c) {
            if (c.dataset.noteType) activeTypes.push(c.dataset.noteType);
          });
          if (activeTypes.length === 0) activeTypes.push('note');
          btn.textContent = 'Holding…';
          btn.disabled = true;
          try {
            await API.post('/note-entry-add', { ref: ref, types: activeTypes, text: text, followUpAt: holdUntil });
            overlay.remove();
            document.removeEventListener('keydown', onEsc);
            // Ryan 2026-05-21: quiet refresh so the row stays expanded and
            // the page doesn't jump to the top after Put on Hold / Edit
            // Hold Date.
            fetchQuotes({ quiet: true });
          } catch (err) {
            btn.textContent = _modalSubmitLabel;
            btn.disabled = false;
            alert('Could not hold: ' + (err && err.message ? err.message : 'Unknown error'));
          }
        }
      });
      function onEsc(ev) {
        if (ev.key === 'Escape') {
          overlay.remove();
          document.removeEventListener('keydown', onEsc);
        }
      }
      document.addEventListener('keydown', onEsc);
      if (ta) setTimeout(function() { ta.focus(); }, 50);

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Calendar input
         handler for the hold modal. Modal lives outside the dashboard
         tbody, so the global custom-date input listener at L2646 doesn't
         see it — we wire one up here directly. Rep-picked weekend dates
         snap to the adjacent weekday (Sat → Fri, Sun → Mon) for
         consistency with the +Nd presets. */
      var _customDate = overlay.querySelector('.rqd-notes-log__followup-custom');
      if (_customDate) {
        _customDate.addEventListener('input', function() {
          if (!_customDate.value) return;
          var snapped = snapYmdToWeekday(_customDate.value);
          if (snapped !== _customDate.value) _customDate.value = snapped;
          _setArmedDate(snapped);
        });
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Disable submit
         button when the textarea is empty (worker requires text). Same
         pattern as the inline composer's input listener. */
      var _submitBtn = overlay.querySelector('#rqd-hold-apply');
      function _syncSubmitEnabled() {
        if (_submitBtn) _submitBtn.disabled = !(ta && ta.value && ta.value.trim());
      }
      if (ta) ta.addEventListener('input', _syncSubmitEnabled);
      _syncSubmitEnabled();
    }

    // Promote a SAMPLE record into the active NEW LEAD queue. Confirms
    // because this commits the rep to a slot — they should mean it.
    async function handlePromoteSample(btn) {
      var ref = btn.dataset.ref;
      document.querySelectorAll('.rqd-actions-menu--open').forEach(function(m) { m.classList.remove('rqd-actions-menu--open'); });
      if (!confirm('Promote ' + ref + ' to NEW LEAD?')) return;
      var origText = btn.textContent;
      btn.textContent = 'Promoting…';
      btn.disabled = true;
      try {
        await API.post('/promote-sample-to-lead', { ref: ref });
        fetchQuotes();
      } catch (err) {
        btn.textContent = origText;
        btn.disabled = false;
        var msg = err && err.message ? err.message : 'Unknown error';
        alert('Could not promote: ' + msg);
      }
    }

    // ── Generate Quote (direct from dashboard) ──


  // === Public API — attach all 6 to window.RQD ===
  window.RQD.handleMarkLeadDead = handleMarkLeadDead;
  window.RQD.handleResurrectLead = handleResurrectLead;
  window.RQD.openLeadActionsModal = openLeadActionsModal;
  window.RQD.handleHoldLead = handleHoldLead;
  window.RQD.handleWakeLead = handleWakeLead;
  window.RQD.openHoldDateModal = openHoldDateModal;

  // === Init — main.js calls this once at boot to inject deps ===
  window.RQD.initLeadActions = function(d) {
    d = d || {};
    esc = d.esc;
    formatDate = d.formatDate;
    formatFollowUpDate = d.formatFollowUpDate;
    daysFromNowYmd = d.daysFromNowYmd;
    snapYmdToWeekday = d.snapYmdToWeekday;
    fetchQuotes = d.fetchQuotes;
    _qstHasContactActivity = d._qstHasContactActivity;
    getAllQuotes = d.getAllQuotes;
    handleBuildQuoteFromLead = d.handleBuildQuoteFromLead;
    API = d.API || (window.RepPortal && window.RepPortal.API);
    Auth = d.Auth || (window.RepPortal && window.RepPortal.Auth);
  };
})();
